import {
  Activity,
  CheckCircle2,
  Database,
  FileStack,
  Server,
  Sparkles,
  XCircle,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Organization } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Common/Card';
import configurationService from '@/services/configurationService';
import forgeService from '@/services/forgeService';
import organizationService from '@/services/organizationService';
import tokenEconomyService, {
  type OrganizationQuotaRow,
} from '@/services/tokenEconomyService';
import { useForgeApiEnabled } from '@/stores/forgeApiStore';
import { CONFIG_FILES } from '@/components/OntologyManagement/configurationData';

interface OrganizationAIProps {
  organization: Organization;
}

interface TemplateLite {
  id: string;
  name: string;
  description: string;
  file_count: number;
}

const formatCompact = (n: number): string =>
  new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(n);

const pickColor = (pct: number): string => {
  if (pct >= 100) return 'hsl(0, 80%, 55%)';
  if (pct >= 90) return 'hsl(0, 75%, 55%)';
  if (pct >= 60) return 'hsl(35, 90%, 50%)';
  return 'hsl(140, 60%, 40%)';
};

const formatPeriod = (start?: string, end?: string): string => {
  if (!start || !end) return '—';
  try {
    const s = new Date(start);
    const e = new Date(end);
    const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return `${fmt(s)} → ${fmt(e)}`;
  } catch {
    return `${start} → ${end}`;
  }
};

const StatusItem: React.FC<{ ok: boolean; label: string; hint?: React.ReactNode }> = ({
  ok,
  label,
  hint,
}) => (
  <li className="flex items-start gap-2 rounded-md border border-border px-3 py-2">
    {ok ? (
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
    ) : (
      <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
    )}
    <div className="min-w-0">
      <div className="text-sm font-medium">{label}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  </li>
);

export const OrganizationAI: React.FC<OrganizationAIProps> = ({ organization }) => {
  const { t } = useTranslation();
  const forgeApiEnabled = useForgeApiEnabled() === true;
  const [quota, setQuota] = useState<OrganizationQuotaRow | null>(null);
  const [period, setPeriod] = useState<{ start?: string; end?: string }>({});
  const [templates, setTemplates] = useState<TemplateLite[]>([]);
  const [uploadedFileCount, setUploadedFileCount] = useState<number>(0);
  const [forgeClientName, setForgeClientName] = useState<string | null>(null);
  const [hasAdmin, setHasAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [quotaResp, templatesResp, configResp, users] = await Promise.all([
          tokenEconomyService.listOrganizationQuotas().catch(() => null),
          configurationService.getTemplates().catch(() => ({ templates: [] as TemplateLite[] })),
          configurationService
            .getOrganizationConfiguration(organization.id)
            .catch(() => ({ files: {} as Record<string, string | null> })),
          organizationService.getOrganizationUsers(organization.id).catch(() => []),
        ]);

        if (cancelled) return;

        if (quotaResp) {
          const row = quotaResp.items.find((r) => r.organization_id === organization.id) ?? null;
          setQuota(row);
          setPeriod({ start: quotaResp.period_start, end: quotaResp.period_end });
        }

        setTemplates(templatesResp.templates ?? []);

        const files = configResp.files ?? {};
        const uploaded = CONFIG_FILES.reduce((count, f) => {
          return files[`${f.id}_path`] ? count + 1 : count;
        }, 0);
        setUploadedFileCount(uploaded);

        setHasAdmin(users.some((u) => u.role === 'admin'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [organization.id]);

  // Kept apart from the main load so a Forge-off deployment never calls the Forge API.
  useEffect(() => {
    let cancelled = false;
    const loadForgeClient = async () => {
      if (!forgeApiEnabled || !organization.forge_client_id) {
        setForgeClientName(null);
        return;
      }
      try {
        const client = await forgeService.getClient(organization.forge_client_id);
        if (!cancelled) setForgeClientName(client.name);
      } catch {
        // Forge unavailable; keep null and show raw id below.
      }
    };
    void loadForgeClient();
    return () => {
      cancelled = true;
    };
  }, [organization.forge_client_id, forgeApiEnabled]);

  const ontologyTemplate = templates.find((tpl) => tpl.id === organization.sector_template_id);

  const allLinked =
    (!forgeApiEnabled || !!organization.forge_client_id) &&
    !!organization.sector_template_id &&
    uploadedFileCount === CONFIG_FILES.length &&
    hasAdmin;

  const usagePct = quota ? Math.min(quota.percentage, 100) : 0;
  const usageColor = quota ? pickColor(quota.percentage) : 'hsl(140, 60%, 40%)';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-5 w-5" />
          {t('organizationAI.title', 'IA & ontologie')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <section>
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Activity className="h-4 w-4" />
            {t('organizationAI.usageTitle', 'Utilisation GenieArtifact')}
          </div>
          {loading && !quota ? (
            <div className="text-sm text-muted-foreground">
              {t('global.loading', 'Chargement...')}
            </div>
          ) : !quota ? (
            <div className="text-sm text-muted-foreground">
              {t('organizationAI.noUsage', 'Aucune donnée d\'utilisation pour cette organisation.')}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <div className="text-xs font-medium text-muted-foreground">
                  {t('organizationAI.tokensUsed', 'Tokens utilisés')}
                </div>
                <div className="mt-0.5 text-2xl font-semibold">{formatCompact(quota.used)}</div>
                <div className="text-xs text-muted-foreground">
                  {t('organizationAI.outOf', 'sur')} {formatCompact(quota.effective_quota)}
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-muted-foreground">
                  {t('organizationAI.consumption', 'Consommation')}
                </div>
                <div className="mt-1 flex items-center justify-between text-sm">
                  <span style={{ color: usageColor }} className="font-semibold">
                    {Math.round(quota.percentage)}%
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {quota.quota_override !== null
                      ? t('organizationAI.overrideQuota', 'Quota personnalisé')
                      : t('organizationAI.defaultQuota', 'Quota par défaut')}
                  </span>
                </div>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full transition-[width] duration-200"
                    style={{ width: `${usagePct}%`, background: usageColor }}
                  />
                </div>
              </div>
              <div>
                <div className="text-xs font-medium text-muted-foreground">
                  {t('organizationAI.period', 'Période')}
                </div>
                <div className="mt-0.5 text-sm">{formatPeriod(period.start, period.end)}</div>
              </div>
            </div>
          )}
        </section>

        <section className="border-t border-border pt-6">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
            <Database className="h-4 w-4" />
            {t('organizationAI.ontologyTitle', "Template d'ontologie")}
          </div>
          {organization.sector_template_id ? (
            <div className="rounded-md border border-border p-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {ontologyTemplate?.name ?? organization.sector_template_id}
                </span>
                {ontologyTemplate && (
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {t('organizationAI.fileCount', '{{count}} fichiers attendus', {
                      count: ontologyTemplate.file_count,
                    })}
                  </span>
                )}
              </div>
              {ontologyTemplate?.description && (
                <div className="mt-1 text-xs text-muted-foreground">
                  {ontologyTemplate.description}
                </div>
              )}
              <div className="mt-2 flex items-center gap-2 text-xs">
                <FileStack className="h-3.5 w-3.5 text-muted-foreground" />
                <span>
                  {t('organizationAI.filesUploaded', '{{uploaded}}/{{total}} fichiers déployés', {
                    uploaded: uploadedFileCount,
                    total: CONFIG_FILES.length,
                  })}
                </span>
                {uploadedFileCount === CONFIG_FILES.length ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-amber-500" />
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground italic">
              {t('organizationAI.noTemplate', 'Aucun template de secteur défini.')}
            </div>
          )}
        </section>

        <section className="border-t border-border pt-6">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <CheckCircle2 className="h-4 w-4" />
              {t('organizationAI.linksTitle', 'État des liaisons')}
            </div>
            {allLinked ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-200">
                <CheckCircle2 className="h-3 w-3" />
                {t('organizationAI.allLinked', 'Tout est lié')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                <XCircle className="h-3 w-3" />
                {t('organizationAI.partiallyLinked', 'Configuration incomplète')}
              </span>
            )}
          </div>
          <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {forgeApiEnabled && (
              <StatusItem
                ok={!!organization.forge_client_id}
                label={t('organizationAI.linkForgeClient', 'Client Forge')}
                hint={
                  organization.forge_client_id ? (
                    <span className="inline-flex items-center gap-1">
                      <Server className="h-3 w-3" />
                      {forgeClientName ?? organization.forge_client_id}
                    </span>
                  ) : (
                    t('organizationAI.notLinked', 'Non lié')
                  )
                }
              />
            )}
            <StatusItem
              ok={!!organization.sector_template_id}
              label={t('organizationAI.linkSectorTemplate', 'Template de secteur')}
              hint={
                organization.sector_template_id
                  ? ontologyTemplate?.name ?? organization.sector_template_id
                  : t('organizationAI.notSet', 'Non défini')
              }
            />
            <StatusItem
              ok={uploadedFileCount === CONFIG_FILES.length}
              label={t('organizationAI.linkOntologyFiles', 'Fichiers d\'ontologie')}
              hint={t('organizationAI.filesUploaded', '{{uploaded}}/{{total}} fichiers déployés', {
                uploaded: uploadedFileCount,
                total: CONFIG_FILES.length,
              })}
            />
            <StatusItem
              ok={hasAdmin}
              label={t('organizationAI.linkAdmin', 'Administrateur')}
              hint={
                hasAdmin
                  ? t('organizationAI.adminAssigned', 'Au moins un admin défini')
                  : t('organizationAI.noAdmin', 'Aucun administrateur défini')
              }
            />
          </ul>
        </section>
      </CardContent>
    </Card>
  );
};

export default OrganizationAI;
